import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useBilling } from "../lib/billing";
import { Heart, MagnifyingGlass, Plus, Clock, Users, BookOpen, CrownSimple, Trash } from "@phosphor-icons/react";
import { SafeRecipeImage } from "../components/SafeRecipeImage";

export default function Library() {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [showFavs, setShowFavs] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { status } = useBilling();

  const load = async () => {
    setLoading(true);
    const [r, c] = await Promise.all([api.get("/recipes"), api.get("/categories")]);
    setRecipes(r.data);
    setCategories(c.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return recipes.filter((r) => {
      if (showFavs && !r.is_favorite) return false;
      if (activeCat !== "all" && r.category_id !== activeCat) return false;
      if (query) {
        const q = query.toLowerCase();
        const hit = r.title.toLowerCase().includes(q)
          || (r.description || "").toLowerCase().includes(q)
          || r.ingredients?.some((i) => i.name.toLowerCase().includes(q));
        if (!hit) return false;
      }
      return true;
    });
  }, [recipes, activeCat, query, showFavs]);

  const toggleFav = async (e, r) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !r.is_favorite;
    setRecipes((rs) => rs.map((x) => x.id === r.id ? { ...x, is_favorite: next } : x));
    try { await api.put(`/recipes/${r.id}`, { is_favorite: next }); } catch { load(); }
  };

  const deleteRecipe = async (e, r) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`¿Eliminar "${r.title}" de tu recetario? Esta acción no se puede deshacer.`)) return;
    const prev = recipes;
    setRecipes((rs) => rs.filter((x) => x.id !== r.id));
    try { await api.delete(`/recipes/${r.id}`); } catch { setRecipes(prev); }
  };

  return (
    <div className="space-y-10" data-testid="library-page">
      {/* Hero / new recipe */}
      <section className="relative rounded-3xl overflow-hidden grain bg-[hsl(var(--muted))]">
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-0">
          <div className="md:col-span-7 p-8 md:p-12">
            <p className="eyebrow mb-3">tu recetario</p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              Tienes <span className="italic text-[hsl(var(--primary))]">{recipes.length}</span> {recipes.length === 1 ? "receta" : "recetas"} en la repisa.
            </h1>
            <p className="mt-4 text-[hsl(var(--muted-foreground))] max-w-md">
              Pega un nuevo enlace de Instagram, TikTok o YouTube y deja que MIZETA haga la magia.
            </p>
            <button
              onClick={() => navigate("/app/extract")}
              data-testid="library-extract-btn"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--foreground))] text-white font-semibold btn-press hover-lift"
            >
              <Plus size={18} weight="bold" /> Extraer una receta
            </button>

            {status && (
              <div className="mt-6 inline-flex flex-wrap items-center gap-3" data-testid="library-usage-pill">
                {status.is_pro ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--secondary))]/15 text-[hsl(var(--secondary))] text-xs font-semibold">
                    <CrownSimple size={14} weight="fill" /> Pro hasta el {new Date(status.pro_until).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                  </span>
                ) : (
                  <>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      {status.extraction_count} / {status.free_limit} recetas gratuitas
                    </span>
                    <Link to="/app/pro" data-testid="library-pro-link" className="text-xs font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1">
                      <CrownSimple size={12} weight="fill" /> Pasa a Pro
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-5 hidden md:block relative min-h-[260px]">
            <img
              src="https://images.unsplash.com/photo-1507048331197-7d4ac70811cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGluZ3JlZGllbnRzJTIwY29va2luZyUyMG92ZXJoZWFkfGVufDB8fHx8MTc3ODg0MzEzMXww&ixlib=rb-4.1.0&q=85"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="pill-input flex items-center gap-2 flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={18} className="text-[hsl(var(--muted-foreground))]" />
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por título o ingrediente…"
              className="bg-transparent outline-none w-full text-sm"
            />
          </div>
          <button
            onClick={() => setShowFavs((s) => !s)}
            data-testid="filter-favs-btn"
            className={`pill-input inline-flex items-center gap-2 text-sm font-medium ${showFavs ? "bg-[hsl(var(--primary))] text-white border-transparent" : ""}`}
          >
            <Heart size={16} weight={showFavs ? "fill" : "regular"} /> Favoritas
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          {[{ id: "all", name: "Todas" }, ...categories].map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              data-testid={`category-chip-${c.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCat === c.id
                  ? "bg-[hsl(var(--foreground))] text-white"
                  : "bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--foreground))]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section>
        {loading ? (
          <div className="text-center py-20 text-[hsl(var(--muted-foreground))]" data-testid="library-loading">Cargando recetas…</div>
        ) : filtered.length === 0 ? (
          <EmptyState onAction={() => navigate("/app/extract")} hasRecipes={recipes.length > 0} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="recipes-grid">
            {filtered.map((r, i) => (
              <Link
                to={`/app/recipe/${r.id}`}
                key={r.id}
                data-testid={`recipe-card-${r.id}`}
                className="group block fade-up"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-[hsl(var(--muted))] hover-lift">
                  {r.image_url ? (
                    <SafeRecipeImage
                      src={r.image_url}
                      alt={r.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      testId={`recipe-cover-${r.id}`}
                      fallback={(
                        <div className="absolute inset-0 flex flex-col gap-2 items-center justify-center text-[hsl(var(--muted-foreground))]" data-testid={`recipe-cover-${r.id}-fallback`}>
                          <BookOpen size={40} weight="thin" />
                          <span className="text-xs">Portada no disponible</span>
                        </div>
                      )}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[hsl(var(--muted-foreground))]">
                      <BookOpen size={40} weight="thin" />
                    </div>
                  )}
                  <button
                    onClick={(e) => toggleFav(e, r)}
                    data-testid={`fav-toggle-${r.id}`}
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center btn-press"
                  >
                    <Heart size={18} weight={r.is_favorite ? "fill" : "regular"} className={r.is_favorite ? "text-[hsl(var(--primary))]" : ""} />
                  </button>
                  <button
                    onClick={(e) => deleteRecipe(e, r)}
                    data-testid={`delete-${r.id}`}
                    aria-label="Eliminar receta"
                    className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center btn-press text-[hsl(var(--destructive))] sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity"
                  >
                    <Trash size={16} />
                  </button>
                </div>
                <h3 className="font-serif text-xl mt-3 leading-snug group-hover:text-[hsl(var(--primary))] transition-colors">{r.title}</h3>
                <div className="mt-2 flex items-center text-xs text-[hsl(var(--muted-foreground))]">
                  {r.servings && (<span className="inline-flex items-center gap-1"><Users size={12} /> {r.servings}</span>)}
                  {r.servings && r.total_time && <span className="divider-dot" />}
                  {r.total_time && (<span className="inline-flex items-center gap-1"><Clock size={12} /> {r.total_time}</span>)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function EmptyState({ onAction, hasRecipes }) {
  return (
    <div className="text-center py-20" data-testid="library-empty">
      <div className="inline-flex w-16 h-16 rounded-full bg-[hsl(var(--muted))] items-center justify-center mb-5">
        <BookOpen size={28} weight="duotone" className="text-[hsl(var(--primary))]" />
      </div>
      <p className="font-serif italic text-3xl">{hasRecipes ? "Sin coincidencias." : "Tu recetario está vacío."}</p>
      <p className="mt-2 text-[hsl(var(--muted-foreground))]">
        {hasRecipes ? "Prueba con otra búsqueda o categoría." : "Pega tu primer enlace para guardar una receta."}
      </p>
      <button onClick={onAction} data-testid="empty-extract-btn" className="mt-6 px-6 py-3 rounded-full bg-[hsl(var(--primary))] text-white font-semibold btn-press hover-lift">
        Extraer una receta
      </button>
    </div>
  );
}
